import { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Form, useFormikContext } from "formik";
import _ from "lodash";
import FluidSelect from "../../../../common/components/FluidSelect";
import { Spacer } from "../../../../common/components/Spacer";
import { DatePickers } from "./DatePickers";
import { CommodityFixing } from "./CommodityFixing";
import { If } from "../../../../common/components/If";
import { Toggle } from "../../../../common/components/Toggle";
import {
	AdjustmentsFrequency,
	SpotMarket,
	TradeType,
} from "../../../../requests_cm/gecoContractsService/types";
import { getSyntheticEvent } from "../../../../common/utils/getSyntheticEvent";
import { FluidNumber } from "../../../../common/components/FluidNumberField";
import { FluidTextField } from "../../../../common/components/FluidTextField";
import { GeneralTabFormikData } from "../../formik/generalTabFormik";
import { isNull } from "../../../../common/utils/isFalsy";
import {
	useGetPricingAddonsQuery,
	useLazyGetBCVConfigQuery,
} from "../../../../requests_cm/gecoReferentialService/service";
import { useRtkQueryDynamicEndpoint } from "../../../../common/hooks/useRtkQueryDynamicEndpoint";
import { mappingBCVConfig } from "./dataMapping";
import CustomAutocomplete from "../../../../common/components/CustomAutocomplete";
import { differenceInHours, parse } from "date-fns";
import { NJCheckbox } from "@engie-group/fluid-design-system-react";
import { PricingAddons } from "../../../../requests_cm/gecoReferentialService/types";

const style = {
	container: {
		display: "flex",
		flexDirection: "column",
		flex: 1,
	},
	submitWrapper: {
		marginLeft: "auto",
	},
	baseprod: {
		display: "flex",
		flexDirection: "row",
		gap: "24px",
		alignItems: "flex-end",
	},
	baseProdCapaMwh: {
		width: "180px",
	},
	pricingAddons: {
		display: "flex",
		flexDirection: "column",
		gap: "24px",
	},
};

interface GeneralTabFormProps {
	setHorizonChangeOpen: (state: boolean) => void;
	country: string;
}
const GeneralTabForm = ({
	setHorizonChangeOpen,
	country,
}: GeneralTabFormProps) => {
	const [
		lazySearchBCVConfig,
		{ data: BCVConfigData, isLoading: loadingBCVConfig },
	] = useLazyGetBCVConfigQuery({
		selectFromResult(result) {
			const formatedData = mappingBCVConfig(result.data);
			return { ...result, data: formatedData };
		},
	});
	const searchBCVConfig = useRtkQueryDynamicEndpoint(lazySearchBCVConfig);
	const [
		lazySearchBCVConfigGoo,
		{ data: BCVConfigDataGoo, isLoading: loadingBCVConfigGoo },
	] = useRtkQueryDynamicEndpoint(useLazyGetBCVConfigQuery)({
		selectFromResult(result) {
			const formatedData = mappingBCVConfig(result.data);
			return { ...result, data: formatedData };
		},
	});
	const searchBCVConfigGoo = useRtkQueryDynamicEndpoint(
		lazySearchBCVConfigGoo
	);

	const { data: pricingAddonsData, isLoading: loadingPricingAddons } =
		useRtkQueryDynamicEndpoint(useGetPricingAddonsQuery)({});

	const { values, handleChange, handleSubmit, dirty, setFieldValue } =
		useFormikContext<GeneralTabFormikData>();

	useEffect(() => {
		if (dirty) handleSubmit();
	}, [dirty, values]);

	const baseProdCapacityMwh = useMemo(() => {
		if (
			!values.start_date ||
			!values.end_date ||
			!values.base_prod_capacity
		) {
			return 0;
		}
		const startDate = parse(values.start_date, "yyyy-MM-dd", new Date());
		const endDate = parse(values.end_date, "yyyy-MM-dd", new Date());

		return (
			values.base_prod_capacity * differenceInHours(endDate, startDate)
		);
	}, [values.base_prod_capacity, values.start_date, values.end_date]);

	return (
		<Form onSubmit={handleSubmit}>
			<Box sx={style.container}>
				<Spacer gap={24} />
				<FluidTextField
					onChange={handleChange}
					value={values.name}
					name={"name"}
					label={"Name"}
					sx={{
						width: "100%",
					}}
					isDisabled
				/>
				<Spacer gap={24} />
				<DatePickers
					onChange={handleChange}
					tradeDate={values.trade_date}
					startDate={values.start_date}
					endDate={values.end_date}
					onOpenChangeHorizon={() => setHorizonChangeOpen(true)}
				/>
				<Spacer gap={24} />
				<Toggle
					onChange={handleChange}
					isTrue={values.test_phase || false}
					name={"test_phase"}
					label={"Test Phase"}
				/>
				<Spacer gap={24} />
				<FluidSelect
					isReadOnly
					isLabelStatic
					items={[
						{
							value: TradeType.PPA_BASE_PHYSICAL,
							label: "Ppa Base Physical",
						},
						{
							value: TradeType.PPA_PHYSICAL,
							label: "Ppa Physical",
						},
					]}
					label={"PPA Type"}
					name={"trade_type"}
					onChange={(value: unknown) => {
						// SelectFluid sends us only the value of the option and not the all input event
						handleChange(getSyntheticEvent("trade_type", value));
					}}
					value={values.trade_type}
				/>
				<Spacer gap={24} />
				<If
					condition={
						values.trade_type === TradeType.PPA_BASE_PHYSICAL
					}
				>
					<Box sx={style.baseprod}>
						<FluidNumber
							onChange={handleChange}
							value={values.base_prod_capacity || 0}
							name={"base_prod_capacity"}
							title={"Base Prod Capacity (MW)"}
						/>
						<FluidNumber
							sx={style.baseProdCapaMwh}
							onChange={handleChange}
							isReadOnly={true}
							value={baseProdCapacityMwh}
							name={"base_prod_capacity_mwh"}
							title={"Base Prod Capacity (MWh)"}
						/>
					</Box>
				</If>
				<Spacer gap={24} />
				<CommodityFixing
					onChange={handleChange}
					commodity={values.commodity}
				/>
				<Spacer gap={24} />
				<Toggle
					name={"invoice_decomposition"}
					label={"Invoice Decomposition"}
					description={
						"True if the invoiced is decomposed by post (Click, Unclick, Negative Price etc) or not"
					}
					onChange={handleChange}
					isTrue={!!values.invoice_decomposition}
				/>
				<Spacer gap={24} />
				<Toggle
					name={"has_adjustments"}
					label={"Has Adjustments"}
					description={
						"For trades with final settlements only : says if monthly settlements are adjusted or not"
					}
					onChange={handleChange}
					isTrue={!!values.has_adjustments}
				/>
				<Spacer gap={24} />
				<FluidSelect
					isLabelStatic
					items={[
						{
							label: AdjustmentsFrequency.MONTHLY,
							value: AdjustmentsFrequency.MONTHLY,
						},
						{
							label: AdjustmentsFrequency.YEARLY,
							value: AdjustmentsFrequency.YEARLY,
						},
						{
							label: AdjustmentsFrequency.QUARTERLY,
							value: AdjustmentsFrequency.QUARTERLY,
						},
						{
							label: AdjustmentsFrequency.DAILY,
							value: AdjustmentsFrequency.DAILY,
						},
						{
							label: AdjustmentsFrequency.WEEKLY,
							value: AdjustmentsFrequency.WEEKLY,
						},
						{
							label: "Semi Annually",
							value: AdjustmentsFrequency.SEMI_ANNUALLY,
						},
						{
							label: "At Maturity",
							value: AdjustmentsFrequency.AT_MATURITY,
						},
					]}
					label={"Adjustment Frequency"}
					name={"adjustment_frequency"}
					description="Used only for BaseProd deals"
					onChange={(value: unknown) => {
						// SelectFluid sends us only the value of the option and not the all input event
						handleChange(
							getSyntheticEvent("adjustment_frequency", value)
						);
					}}
					value={values.adjustment_frequency}
				/>
				<Spacer gap={24} />
				<CustomAutocomplete
					data={BCVConfigData || []}
					fetchData={(long_name: string) => {
						const searchName = long_name.split("|")[0].trimEnd();
						searchBCVConfig({
							search__name: searchName,
						});
					}}
					label="BCV config"
					name="bcv_config"
					setFieldValue={setFieldValue}
					loading={loadingBCVConfig}
					value={values.bcv_config}
				/>
				<If condition={!isNull(values?.goo_price)}>
					<Spacer gap={8} />
					<FluidNumber
						onChange={handleChange}
						name={"goo_price"}
						title={"GOO Price"}
						value={values.goo_price || 0}
					/>
				</If>
				<If condition={values.goo_price && values.goo_price > 0}>
					<Spacer gap={24} />

					<CustomAutocomplete
						data={BCVConfigDataGoo || []}
						fetchData={(long_name: string) => {
							const searchName = long_name
								.split("|")[0]
								.trimEnd();
							searchBCVConfigGoo({
								search__name: searchName,
							});
						}}
						label="BCV config Goo"
						name="bcv_config_goo"
						setFieldValue={setFieldValue}
						loading={loadingBCVConfigGoo}
						value={values.bcv_config_goo}
					/>
				</If>
				<If condition={(country ?? "").toUpperCase() === "US"}>
					<Spacer gap={24} />
					<If condition={loadingPricingAddons}>Loading...</If>
					<If condition={!loadingPricingAddons && pricingAddonsData}>
						<Box sx={style.pricingAddons}>
							<Typography variant="h6">Pricing Addons</Typography>
							{pricingAddonsData?.map((addon: PricingAddons) => (
								<>
									<p>{addon.code}</p>
									<NJCheckbox
										key={addon.id}
										label={addon.name + ` (${addon.code})`}
										inputId={addon.code}
										data-testid={`pricing-addons-${addon.code}`}
										value={
											values.pricing_addons.some(
												(addonSelected) =>
													addonSelected.id ===
													addon.id
											)
												? "on"
												: "off"
										}
										isChecked={values.pricing_addons.some(
											(addonSelected) =>
												addonSelected.id === addon.id
										)}
										onChange={(ev) => {
											ev.preventDefault();
											setFieldValue(
												"pricing_addons",
												_.xorBy(
													values.pricing_addons,
													[addon],
													"id"
												)
											);
										}}
									/>
								</>
							))}
						</Box>
					</If>
					<Spacer gap={24} />
					<FluidSelect
						isLabelStatic
						items={[
							{
								label: SpotMarket.DA,
								value: SpotMarket.DA,
							},
							{
								label: SpotMarket.RT,
								value: SpotMarket.RT,
							},
							{
								label: SpotMarket.NONE,
								value: SpotMarket.NONE,
							},
						]}
						label={"Spot market"}
						name={"spot_market"}
						onChange={(value: unknown) => {
							// SelectFluid sends us only the value of the option and not the all input event
							handleChange(
								getSyntheticEvent("spot_market", value)
							);
						}}
						value={values.spot_market}
					/>
				</If>
			</Box>
		</Form>
	);
};

export default GeneralTabForm;
